import { useCallback, useEffect, useRef, useState } from "react";
import type { AiGrantAccess } from "@excalidraw-ms/shared";
import * as backendClient from "../api/backendClient";

/** Caller-bound grants for the open drawing; merely loading never grants access. */
export function useDrawingAiSharing({
  drawingId,
  accountId,
  enabled,
  getToken,
}: {
  drawingId?: string;
  accountId?: string;
  enabled: boolean;
  getToken: () => Promise<string>;
}) {
  const [grant, setGrant] = useState<{ code: string; access: AiGrantAccess | null } | null>(null);
  const [loading, setLoading] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [externalRevocationCount, setExternalRevocationCount] = useState(0);
  const grantRef = useRef<{ code: string; access: AiGrantAccess | null } | null>(null);
  const request = useRef(0);
  const mutating = useRef(false);
  const invalidate = useCallback(() => ++request.current, []);

  useEffect(() => {
    invalidate();
    mutating.current = false;
    grantRef.current = null;
    setGrant(null);
    setError(null);
    setPreparing(false);
    setLoading(enabled && !!drawingId);
    if (!enabled || !drawingId) return;
    let checking = false;
    const refresh = async () => {
      if (checking || mutating.current) return;
      checking = true;
      const version = ++request.current;
      try {
        const token = await getToken();
        const result = await backendClient.lookupAiGrants(token, [drawingId]);
        if (version !== request.current) return;
        const active = result.grants.find(
          (item) => item.drawingId === drawingId && item.status === "active",
        );
        const nextGrant = active ? { code: active.code, access: active.access } : null;
        if (grantRef.current && !nextGrant) {
          setExternalRevocationCount((count) => count + 1);
        }
        grantRef.current = nextGrant;
        setGrant(nextGrant);
        setError(null);
      } catch {
        if (version === request.current) {
          setError("AI access could not be checked. Share with AI to retry.");
        }
      } finally {
        checking = false;
        if (version === request.current) setLoading(false);
      }
    };
    void refresh();
    const interval = window.setInterval(() => void refresh(), 30_000);
    const onFocus = () => void refresh();
    window.addEventListener("focus", onFocus);
    return () => {
      invalidate();
      window.clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
  }, [drawingId, accountId, enabled, getToken, invalidate]);

  const share = useCallback(async () => {
    if (!enabled || !drawingId) throw new Error("Drawing access is not ready");
    const version = ++request.current;
    mutating.current = true;
    setError(null);
    setLoading(true);
    setPreparing(true);
    try {
      const token = await getToken();
      const result = await backendClient.mintAiGrant(token, drawingId);
      if (version === request.current) {
        const nextGrant = { code: result.grant.code, access: result.access };
        grantRef.current = nextGrant;
        setGrant(nextGrant);
      }
    } catch (error) {
      if (version === request.current) {
        setError("AI access could not be prepared. Please try Share with AI again.");
      }
      throw error;
    } finally {
      if (version === request.current) {
        setLoading(false);
        setPreparing(false);
        mutating.current = false;
      }
    }
  }, [drawingId, enabled, getToken]);

  const revoke = useCallback(async () => {
    if (!grant || !enabled) throw new Error("AI access is not ready");
    const version = ++request.current;
    mutating.current = true;
    try {
      const token = await getToken();
      await backendClient.revokeAiGrant(token, grant.code);
      if (version === request.current) {
        grantRef.current = null;
        setGrant(null);
        setError(null);
      }
    } finally {
      if (version === request.current) mutating.current = false;
    }
  }, [enabled, getToken, grant]);

  return { grant, loading, preparing, error, externalRevocationCount, share, revoke };
}
